import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import {
  FilterTabs,
  PaginationControls,
  SearchBar,
  SortableHeader,
  type SortDirection,
  usePaginatedRows,
  useTableControls,
} from '../components/TableControls';
import { adapter } from '../data/mockAdapter';
import type { CfModuleWhitelistEntry, CfModuleType } from '@ascend/mocked-shared/mock-data';

// ─── Constants ──────────────────────────────────────────────────────────────

const MODULE_TYPES: CfModuleType[] = ['Fee', 'Liquidity', 'Risk', 'Valuation', 'Auction', 'Oracle', 'Compliance'];

type FilterValue = 'All' | CfModuleType;

const FILTER_OPTIONS: { value: FilterValue; label: string }[] = [
  { value: 'All', label: 'All' },
  ...MODULE_TYPES.map((t) => ({ value: t as FilterValue, label: t })),
];

const TYPE_BADGE: Record<CfModuleType, string> = {
  Fee: 'bg-green-100 text-green-700',
  Liquidity: 'bg-blue-100 text-blue-700',
  Risk: 'bg-red-100 text-red-700',
  Valuation: 'bg-purple-100 text-purple-700',
  Auction: 'bg-yellow-100 text-yellow-700',
  Oracle: 'bg-orange-100 text-orange-700',
  Compliance: 'bg-indigo-100 text-indigo-700',
};

type SortKey = 'name' | 'moduleType' | 'chainAddress' | 'chainId' | 'updatedAt';

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

// ─── Modal ──────────────────────────────────────────────────────────────────

function Modal({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-50 w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="streamline:interface-delete-2-remove-bold-add-button-buttons-delete" className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function ModuleManagementPage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState<CfModuleWhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterValue>('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CfModuleWhitelistEntry | null>(null);

  const { query, setQuery, page, setPage, pageSize, setPageSize } = useTableControls();
  const [sortKey, setSortKey] = useState<SortKey>('moduleType');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // Form state for add/edit
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<CfModuleType>('Fee');
  const [formDescription, setFormDescription] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formChainId, setFormChainId] = useState('11155111');

  const loadModules = useCallback(async () => {
    const data = await adapter.modules.list();
    setModules(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await adapter.modules.list();
      if (cancelled) return;
      setModules(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    let result = modules;
    if (activeFilter !== 'All') result = result.filter((m) => m.moduleType === activeFilter);
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.chainAddress.toLowerCase().includes(q),
      );
    }
    return result;
  }, [modules, activeFilter, query]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'moduleType': cmp = a.moduleType.localeCompare(b.moduleType); break;
        case 'chainAddress': cmp = a.chainAddress.localeCompare(b.chainAddress); break;
        case 'chainId': cmp = a.chainId.localeCompare(b.chainId); break;
        case 'updatedAt': cmp = a.updatedAt.localeCompare(b.updatedAt); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const { rows: paginatedModules } = usePaginatedRows(sorted, page, pageSize);

  function handleSort(key: string) {
    const k = key as SortKey;
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
    setPage(1);
  }

  function resetForm() {
    setFormName('');
    setFormType('Fee');
    setFormDescription('');
    setFormAddress('');
    setFormChainId('11155111');
  }

  async function handleAdd() {
    if (!formName.trim() || !formAddress.trim()) {
      toast.error('Name and chain address are required');
      return;
    }
    await adapter.modules.create({
      moduleType: formType,
      name: formName.trim(),
      description: formDescription.trim(),
      chainAddress: formAddress.trim(),
      chainId: formChainId.trim(),
    });
    toast.success(`Module "${formName}" added`);
    setShowAddModal(false);
    resetForm();
    await loadModules();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await adapter.modules.delete(deleteTarget.id);
    toast.success(`Module "${deleteTarget.name}" removed`);
    setDeleteTarget(null);
    await loadModules();
  }

  function truncateAddress(addr: string): string {
    if (addr.length <= 16) return addr;
    return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">Module Whitelist</h1>
        <p className="text-muted-foreground">Loading modules...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:module-three" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Module Whitelist</h1>
            <p className="text-sm text-muted-foreground">
              Manage whitelisted modules for credit facility deployment. Multiple modules of the same type can exist.
            </p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="flex items-center gap-2">
          <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="h-4 w-4" /> Add Module
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterTabs<FilterValue> filters={FILTER_OPTIONS} active={activeFilter} onChange={(v) => { setActiveFilter(v); setPage(1); }} />
        <SearchBar value={query} onChange={(q: string) => { setQuery(q); setPage(1); }} placeholder="Search modules..." />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <SortableHeader label="Name" sortKey="name" activeSortKey={sortKey} direction={sortDir} onSort={handleSort} />
              <SortableHeader label="Type" sortKey="moduleType" activeSortKey={sortKey} direction={sortDir} onSort={handleSort} />
              <SortableHeader label="Chain Address" sortKey="chainAddress" activeSortKey={sortKey} direction={sortDir} onSort={handleSort} />
              <SortableHeader label="Chain ID" sortKey="chainId" activeSortKey={sortKey} direction={sortDir} onSort={handleSort} />
              <SortableHeader label="Updated" sortKey="updatedAt" activeSortKey={sortKey} direction={sortDir} onSort={handleSort} />
              <th className="px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedModules.map((mod) => (
              <tr
                key={mod.id}
                className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => navigate(`/module-management/${mod.id}`)}
              >
                <td className="px-3 py-2.5 font-medium">{mod.name}</td>
                <td className="px-3 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_BADGE[mod.moduleType]}`}>
                    {mod.moduleType}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-mono text-xs" title={mod.chainAddress}>{truncateAddress(mod.chainAddress)}</td>
                <td className="px-3 py-2.5 text-xs">{mod.chainId}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{new Date(mod.updatedAt).toLocaleDateString()}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setDeleteTarget(mod)}
                      className="rounded-md p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <Icon name="streamline:interface-delete-bin-5-remove-delete-empty-bin-trash-garbage" className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paginatedModules.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  No modules found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalItems={sorted.length} label="modules" />

      {/* Add Module Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)} title="Add Module">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">Module Type</label>
            <select className={INPUT_CLASS} value={formType} onChange={(e) => setFormType(e.target.value as CfModuleType)}>
              {MODULE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Name</label>
            <input type="text" className={INPUT_CLASS} placeholder="e.g. Dutch Auction v3" value={formName} onChange={(e) => setFormName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Description</label>
            <input type="text" className={INPUT_CLASS} placeholder="Brief description..." value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Chain Address</label>
            <input type="text" className={INPUT_CLASS} placeholder="0x..." value={formAddress} onChange={(e) => setFormAddress(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Chain ID</label>
            <input type="text" className={INPUT_CLASS} placeholder="11155111" value={formChainId} onChange={(e) => setFormChainId(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button className="flex-1" onClick={handleAdd}>Add Module</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Confirm Deletion">
        <div className="space-y-4">
          <p className="text-sm">
            Are you sure you want to remove <span className="font-semibold">{deleteTarget?.name}</span> from the module whitelist? This will also remove it from any assigned credit facilities.
          </p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" className="flex-1" onClick={handleDelete}>Delete Module</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
